'use client';
import type { FC, ReactNode } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

interface SwipeProps {
  children: ReactNode;
  className?: string;
  hideControls?: boolean;
  alignToCenter?: boolean;
  arrows?: {
    left: ReactNode;
    right: ReactNode;
  };
}

const Swiper: FC<SwipeProps> = ({
  children,
  className,
  hideControls = false,
  alignToCenter = false,
}) => {
  const startXRef = useRef<number>(0);
  const isDown = useRef<boolean>(false);
  const scrollLeftRef = useRef<number>(0);
  const [showLeft, setShowLeft] = useState(true);
  const [showRight, setShowRight] = useState(true);
  const [centered, setCentered] = useState(alignToCenter);

  const sliderRef = useRef<HTMLDivElement>(null);

  function handlePageScroll(type: 'next' | 'prev'): void {
    if (sliderRef.current) {
      if (type === 'prev') {
        sliderRef.current.scrollTo({
          top: 0,
          behavior: 'smooth',
          left: (sliderRef.current.scrollLeft -=
            sliderRef.current.offsetWidth - 200),
        });
      } else {
        sliderRef.current.scrollTo({
          top: 0,
          behavior: 'smooth',
          left: (sliderRef.current.scrollLeft +=
            sliderRef.current.offsetWidth - 200),
        });
      }

      sliderRef.current.style.scrollBehavior = 'auto';
      setTimeout(() => {
        setShowLeft((sliderRef.current?.scrollLeft || 0) > 0);
        setShowRight(
          sliderRef.current?.scrollWidth !==
            (sliderRef.current?.offsetWidth || 0) +
              (sliderRef.current?.scrollLeft || 0),
        );
      });
    }
  }

  // Arrows
  useEffect(() => {
    if (sliderRef.current) {
      setShowLeft(sliderRef.current.scrollLeft > 0);
      setShowRight(
        sliderRef.current.scrollWidth !==
          (sliderRef.current.offsetWidth || 0) +
            (sliderRef.current.scrollLeft || 0),
      );

      sliderRef.current.onscroll = () => {
        if (sliderRef.current) {
          setShowLeft(sliderRef.current.scrollLeft > 0);
          setShowRight(
            sliderRef.current.scrollWidth !==
              (sliderRef.current.offsetWidth || 0) +
                (sliderRef.current.scrollLeft || 0),
          );
        }
      };
    }
  }, []);

  // Align center
  useLayoutEffect(() => {
    if (alignToCenter && sliderRef.current) {
      setCentered(
        sliderRef.current.scrollWidth <= sliderRef.current.clientWidth,
      );

      window.addEventListener('resize', () => {
        if (sliderRef.current) {
          setCentered(
            sliderRef.current.scrollWidth <= sliderRef.current.clientWidth,
          );
        }
      });
    }

    return () => {
      window.removeEventListener('resize', () => {
        setCentered(alignToCenter);
      });
    };
  }, [alignToCenter]);

  return (
    <div className="relative select-none ">
      <div
        aria-controls="slider-controls"
        aria-valuenow={showLeft ? 1 : 0}
        className={`${centered ? 'justify-center' : ''} flex overflow-x-auto overflow-y-hidden hide-scrollbar will-change-transform 
        transition-all duration-300 ${
          sliderRef.current &&
          sliderRef.current.scrollWidth > sliderRef.current.clientWidth
            ? 'cursor-grabbing'
            : ''
        } relative ${className} sm:snap-none snap-x`}
        onMouseDown={(e) => {
          if (!sliderRef.current) {
            return;
          }
          isDown.current = true;
          const startX = e.pageX - sliderRef.current.offsetLeft;
          startXRef.current = startX;
          const scrollLeft = sliderRef.current.scrollLeft;
          scrollLeftRef.current = scrollLeft;
        }}
        onMouseLeave={() => {
          isDown.current = false;
          setShowLeft((sliderRef.current?.scrollLeft || 0) > 0);
          setShowRight(
            sliderRef.current?.scrollWidth !==
              (sliderRef.current?.offsetWidth || 0) +
                (sliderRef.current?.scrollLeft || 0),
          );
        }}
        onMouseMove={(e) => {
          if (!isDown.current || !sliderRef.current) {
            return;
          }

          e.preventDefault();
          const x = e.pageX - sliderRef.current.offsetLeft;

          const walk = (x - startXRef.current) * 1;
          sliderRef.current.scrollLeft = scrollLeftRef.current - walk;
        }}
        onMouseUp={() => {
          isDown.current = false;
          setShowLeft((sliderRef.current?.scrollLeft || 0) > 0);
          setShowRight(
            sliderRef.current?.scrollWidth !==
              (sliderRef.current?.offsetWidth || 0) +
                (sliderRef.current?.scrollLeft || 0),
          );
        }}
        ref={sliderRef}
        role="scrollbar"
        tabIndex={0}
      >
        {children}
      </div>
      {!hideControls ? (
        <>
          {sliderRef.current &&
          sliderRef.current.scrollWidth > sliderRef.current.clientWidth ? (
            <div>
              {showLeft ? (
                <div className="absolute top-0 left-0 sm:flex items-center h-full  cursor-default">
                  <button
                    className="arrowicon-right"
                    onClick={() => {
                      handlePageScroll('prev');
                    }}
                    onMouseDown={() => {
                      if (sliderRef.current) {
                        sliderRef.current.style.scrollBehavior = 'smooth';
                      }
                    }}
                    type="button"
                  />
                </div>
              ) : null}
              {showRight ? (
                <div className="absolute top-0 right-0 sm:flex items-center h-full  cursor-default">
                  <button
                    className="arrowicon-left"
                    onClick={() => {
                      handlePageScroll('next');
                    }}
                    onMouseDown={() => {
                      if (sliderRef.current) {
                        sliderRef.current.style.scrollBehavior = 'smooth';
                      }
                    }}
                    type="button"
                  />
                </div>
              ) : null}
            </div>
          ) : (
            ''
          )}
        </>
      ) : (
        ''
      )}
    </div>
  );
};

export default Swiper;
